// DTI engine for multi-borrower (V2) loans.
//
// Reads the split-table V2 application directly — every participant, joint
// tradelines and jointly owned properties counted once — and applies the
// agency rules selected by `program`. Pure: no React, no I/O, so the calculator,
// the header and the Rates tab can all share one number.
//
// Rules and citations: web/docs/dti-calculation-agency-rules.md

export const PROGRAMS = ["fnma", "fhlmc", "fha", "va", "usda"]
export const PROGRAM_LABELS = { fnma: "Fannie Mae", fhlmc: "Freddie Mac", fha: "FHA", va: "VA", usda: "USDA" }

// Program rule table. Limits are percentages (null = no limit in the guide /
// defer to AUS findings). See section B of the rules doc for each citation.
const RULES = {
    fnma: {
        installmentShortTerm: "le10", // B3-6-05 p.537
        revolvingNoPaymentPct: 0.05, // B3-6-05 p.538
        studentZeroPaymentPct: 0.01, // B3-6-05 p.538-539
        open30: "exclude", // p.538
        alimonyAsIncomeReduction: false, // lender option, p.533
        nonOccupantHousingInFront: false, // B3-6-02 p.525
        departingRental: "offset", // B3-3.8-05 p.423
        // Non-subject rental properties are netted one by one, then combined (B3-3.1-08, Selling Guide 2026-09-02 p.410).
        combineRentals: true,
        limits: { manual: { front: null, back: 45, occupantBack: 43 }, aus: { front: null, back: 50 } },
    },
    fhlmc: {
        installmentShortTerm: "le10", // 5401.2(b)
        revolvingNoPaymentPct: 0.05,
        studentZeroPaymentPct: 0.005, // Guide p.5401-7
        open30: "fullBalance", // Guide p.5401-10
        alimonyAsIncomeReduction: true, // must reduce income, Guide p.5401-9
        nonOccupantHousingInFront: true, // 5401.1(c)
        departingRental: "net", // lease required, Ch. 5306
        combineRentals: true, // 5306.1(b), p.5306-50: per property, then the combined result
        limits: { manual: { front: 28, back: 45, occupantFront: 35, occupantBack: 43 }, aus: { front: null, back: 50 } },
    },
    fha: {
        installmentShortTerm: "le10and5pct", // 4000.1 p.238
        revolvingNoPaymentPct: 0.05, // p.243
        studentZeroPaymentPct: 0.005, // p.242
        open30: "exclude", // when paid in full monthly, p.244
        alimonyAsIncomeReduction: false, // p.240
        nonOccupantHousingInFront: false, // not stated in the guide (rules doc D.5)
        departingRental: "none", // only with >100 mi relocation + 25% equity, p.267
        limits: { manual: { front: 31, back: 43 }, aus: { front: null, back: null } },
    },
    va: {
        installmentShortTerm: "lt10", // significant if >=10 months, Ch.4 Topic 5.c
        revolvingNoPaymentPct: 0.05, // not stated (rules doc D.4)
        studentAnnualPct: 0.05, // 5% x balance / 12, p.98
        open30: "fivePct", // unless funds verified, p.99
        alimonyAsIncomeReduction: false, // may reduce income, p.97
        nonOccupantHousingInFront: false,
        departingRental: "offset", // Topic 2.n p.87
        limits: { manual: { front: null, back: 41 }, aus: { front: null, back: 41 } },
    },
    usda: {
        installmentShortTerm: "le10",
        revolvingNoPaymentPct: 0.05,
        studentZeroPaymentPct: 0.005,
        open30: "exclude",
        alimonyAsIncomeReduction: false,
        nonOccupantHousingInFront: false,
        departingRental: "offset",
        limits: { manual: { front: 29, back: 41 }, aus: { front: 29, back: 41 } }, // HB-1-3555 p.187/189
    },
}

const COMMUNITY_PROPERTY_STATES = ["AZ", "CA", "ID", "LA", "NV", "NM", "TX", "WA", "WI", "ARIZONA", "CALIFORNIA", "IDAHO", "LOUISIANA", "NEVADA", "NEW MEXICO", "TEXAS", "WASHINGTON", "WISCONSIN"]
const STUDENT_LOAN_PATTERN = /student|navient|nelnet|great lakes|sallie|dept of ed|department of education|mohela|aidvantage|edfinancial|fedloan|educational/i
const OTHER_LIABILITY_LABELS = {
    alimony: "Alimony",
    childsupport: "Child support",
    maintenancepayments: "Separate maintenance",
    jobrelatedexpenses: "Job-related expenses",
    other: "Other",
}

const num = (value) => {
    if (value === null || value === undefined || value === "") return 0
    const parsed = parseFloat(String(value).replace(/[,\s$]/g, ""))
    return Number.isFinite(parsed) ? parsed : 0
}
const list = (value) => (Array.isArray(value) ? value.filter(Boolean) : [])
const round2 = (value) => Math.round(value * 100) / 100
const addressKey = (address) => `${address || ""}`.trim().toLowerCase()

// 1003 V2 convention: a value is monthly unless its period says yearly.
const monthly = (value, period) => (`${period || ""}`.toLowerCase() === "yearly" ? num(value) / 12 : num(value))

/** Default program from the loan product / selected loan type; the LO can switch it. */
export const defaultProgram = (application) => {
    const selection = application?.selection || {}
    const selected = selection.selectedloan || {}
    const hash = selected.hash || selected._hash
    const card = list(selection.loSelectedLoans).find((loan) => hash && (loan.hash || loan._hash) === hash) || selected
    // The selected loan wins over the 1003's loan type. Mtype values carry subtypes
    // (FHA_HIGH_BALANCE, VA_IRRRL), so "_" counts as a separator, not a word character.
    const has = (text, word) => new RegExp(`(^|[^A-Z])${word}([^A-Z]|$)`).test(text)
    const detect = (text) => {
        if (has(text, "FHA")) return "fha"
        if (has(text, "VA")) return "va"
        if (has(text, "USDA") || has(text, "RURAL")) return "usda"
        return ""
    }
    const fromLoan = detect([card.Mtype, card.mtype, card.productname, card.name].filter(Boolean).join(" ").toUpperCase())
    if (fromLoan) return fromLoan
    if (card.Mtype || card.mtype) return "fnma"
    return detect(`${application?.product?.mortgageapplied || ""}`.toUpperCase()) || "fnma"
}

const participantsOf = (application) => {
    const byId = application?.participantsById || {}
    const ids = []
    list(application?.participantOrder).concat(Object.keys(byId)).forEach((id) => {
        if (id && byId[id] && !ids.includes(id)) ids.push(id)
    })
    const primaryId = application?.primaryPartyId || ids[0] || ""
    ids.sort((a, b) => (a === primaryId ? -1 : b === primaryId ? 1 : 0))
    const subjectIsPrimaryResidence = (application?.property?.occupancy || "principal") === "principal"
    return ids.map((id, index) => {
        const person = byId[id]
        const declarations = application?.declarationsByPartyId?.[id] || person.declarations || {}
        // Same fallback as the loan header: a borrower added but not named yet is numbered, not "Borrower".
        const fallback = index === 0 ? "Borrower" : `Co-borrower ${index}`
        const fullName = [person.firstName, person.lastName].filter(Boolean).join(" ")
        return {
            partyId: id,
            name: fullName || `${fallback} (name missing)`,
            shortName: `${person.firstName || ""}`.trim() || fullName || fallback,
            isPrimary: id === primaryId,
            // Only an explicit "no" makes someone a non-occupant; unanswered stays occupant.
            occupant: subjectIsPrimaryResidence && `${declarations.occupyasprimary || ""}`.toLowerCase() !== "no",
            person,
        }
    })
}

/**
 * @param application V2 application (participantsById, finance, property, selection, ...)
 * @param options
 *   program        fnma | fhlmc | fha | va | usda (default: defaultProgram)
 *   underwriting   manual | aus (default manual)
 *   payment        { total, breakdown, isEstimate } subject PITIA from the estimator
 *   excluded       ids of rows the LO unchecked
 *   pendingSaleContract  property keys with an executed sale contract
 *   pricing        inputs for the pricing engine, which adds the product's P&I + MI
 *                  itself (payment.total should then be taxes + insurance + HOA only)
 */
export const computeDTI = (application, options = {}) => {
    const program = PROGRAMS.includes(options.program) ? options.program : defaultProgram(application)
    const underwriting = options.underwriting === "aus" ? "aus" : "manual"
    const rules = RULES[program]
    const excluded = new Set(options.excluded || [])
    const contracts = new Set(options.pendingSaleContract || [])
    const participants = participantsOf(application)
    const occupantIds = new Set(participants.filter((p) => p.occupant).map((p) => p.partyId))
    const property = application?.property || {}
    const subjectOccupancy = property.occupancy || "principal"
    const finance = application?.finance || {}
    const warnings = []

    const income = []
    const housing = []
    const debts = []

    const push = (rows, row) => {
        rows.push({ ...row, monthly: round2(row.monthly), excluded: excluded.has(row.id) })
    }

    // ---- Income ---------------------------------------------------------------
    participants.forEach(({ partyId, person }) => {
        const jobs = [person.occupation, ...list(person.otheroccupations)].filter((job) => job && typeof job === "object")
        jobs.forEach((job, index) => {
            const where = job.employername || (index === 0 ? "Current job" : `Additional job ${index}`)
            if (job.selfemployed) {
                if (job.incomeorloss === "" || job.incomeorloss == null) return
                const share = num(job.percentownership)
                push(income, {
                    id: `inc:${partyId}:job:${job.key || index}`,
                    partyIds: [partyId],
                    category: "selfEmployment",
                    jobIndex: index,
                    label: where,
                    monthly: monthly(job.incomeorloss, job.incomeorlossperiod),
                    note: `Self-employed${share ? `, ${share}% owner` : ""}: amount as entered in the 1003`,
                    needsCashFlow: true,
                })
                return
            }
            const pay = job.income || {}
            const amount = ["base", "overtime", "bonuses", "commissions", "military", "other"]
                .reduce((sum, key) => sum + monthly(pay[key], pay[`${key}monthly`]), 0)
            if (!amount) return
            push(income, {
                id: `inc:${partyId}:job:${job.key || index}`,
                partyIds: [partyId],
                category: "employment",
                jobIndex: index,
                label: where,
                monthly: amount,
            })
        })
        list(person.income?.otherincome).forEach((item, index) => {
            const amount = monthly(item.amount, item.period)
            if (!amount) return
            push(income, {
                id: `inc:${partyId}:other:${item.key || index}`,
                partyIds: [partyId],
                category: "other",
                label: item.description || item.incometype || "Other income",
                monthly: amount,
            })
        })
    })

    // ---- Owned properties (REO): de-duplicate joint ownership by key/address ----
    const properties = []
    const seenProperties = new Set()
    const subjectAddress = addressKey(property.address)
    Object.entries(finance.realEstateByPartyId || {}).forEach(([partyId, realEstate]) => {
        list(realEstate?.properties).forEach((item) => {
            const key = item.key || addressKey(item.address || item.street)
            if (!key || seenProperties.has(key)) return
            // The subject itself is sometimes listed under REO; it is counted as the subject.
            const own = addressKey(item.address || item.street)
            if (subjectAddress && own && (own.startsWith(subjectAddress) || subjectAddress.startsWith(own))) return
            seenProperties.add(key)
            properties.push({ ...item, key, ownerPartyIds: list(item.ownerPartyIds).length ? item.ownerPartyIds : [partyId] })
        })
    })
    // A property's mortgage is counted with the property; the same loan also shows up
    // as a credit tradeline, sometimes under a different key but the same account.
    const propertyLoanKeys = new Set()
    const propertyLoanAccounts = new Set()
    const accountOf = (loan) => {
        const account = `${loan?.accountNumber || ""}`.trim()
        return account ? `${account}|${`${loan.name || loan.creditor || ""}`.trim().toLowerCase()}` : ""
    }
    const rememberLoan = (loan) => {
        if (loan.key) propertyLoanKeys.add(loan.key)
        // The subject's copy of a 2c mortgage is stored as subject-mortgage:<2c key>.
        if (loan.key && loan.key.startsWith("subject-mortgage:")) propertyLoanKeys.add(loan.key.slice("subject-mortgage:".length))
        if (accountOf(loan)) propertyLoanAccounts.add(accountOf(loan))
    }
    const isPropertyLoan = (loan) => (loan.key && propertyLoanKeys.has(loan.key)) || (accountOf(loan) && propertyLoanAccounts.has(accountOf(loan)))
    properties.forEach((item) => list(item.loans).forEach(rememberLoan))
    list(property.loans).forEach(rememberLoan)

    // Fannie / Freddie rental properties, combined after the loop.
    const rentals = []
    const escrowUnknown = []
    properties.forEach((item) => {
        const owners = item.ownerPartyIds
        const label = item.address || item.street || "Owned property"
        const liens = list(item.loans).filter((loan) => `${loan.paidOffAtClosing || ""}`.toLowerCase() !== "yes")
        const loanPayments = liens.reduce((sum, loan) => sum + num(loan.monthlyPayment), 0)
        // URLA 3a: the expense field is insurance, taxes, dues etc. *not included in the mortgage
        // payment*. If a mortgage payment includes taxes and insurance (escrow) and the LO also put
        // them in that field (the insurance / tax breakdown shows them), they are counted once.
        const paying = liens.filter((loan) => num(loan.monthlyPayment) > 0)
        const escrowed = paying.some((loan) => loan.paymentIncludesTaxesInsurance === true)
        const expenseTotal = num(item.insuranceMaintenanceTaxes)
        const taxesInsurance = num(item.insurancemain) + num(item.insurancetax)
        const expenses = escrowed && taxesInsurance > 0 && expenseTotal >= taxesInsurance ? expenseTotal - taxesInsurance : expenseTotal
        const escrowNote = escrowed && expenses !== expenseTotal
            ? `Mortgage payment includes taxes and insurance ($${round2(taxesInsurance).toLocaleString()} not added again)`
            : ""
        if (item.status !== "sold" && expenseTotal > 0 && paying.some((loan) => loan.paymentIncludesTaxesInsurance !== true && loan.paymentIncludesTaxesInsurance !== false)) {
            escrowUnknown.push(label)
        }
        const pitia = loanPayments + expenses
        const id = `reo:${item.key}`
        if (item.status === "sold") return
        if (item.status === "pending") {
            if (contracts.has(item.key) && (program === "fnma" || program === "fhlmc")) return
            if (!pitia) return
            push(debts, {
                id, partyIds: owners, category: "property", label,
                monthly: pitia,
                note: program === "fnma" || program === "fhlmc"
                    ? "Pending sale: counted until there is an executed sale contract with financing contingencies cleared"
                    : "Pending sale: payment counted",
                propertyKey: item.key, pendingSale: true,
            })
            return
        }
        // The borrower's current home is the REO row keyed current-residence:<partyId>.
        // homePartyId only says whose list the row is in; every row has one.
        const isHome = `${item.key || ""}`.startsWith("current-residence:")
        const rentalApplies = item.occupancy === "investment" || item.propertyType === "twotofour"
        const gross = num(item.grossRentalIncome)
        if (rentalApplies && (gross || item.netRentalIncome)) {
            const departing = isHome && item.occupancy === "investment"
            const lenderNet = item.netRentalIncome !== "" && item.netRentalIncome != null
            let net = lenderNet ? num(item.netRentalIncome) : 0.75 * gross - pitia
            let note = lenderNet ? "Net rental income from the 1003 (lender calculation)" : `75% of $${gross.toLocaleString()} rent − $${round2(pitia).toLocaleString()} PITIA`
            if (escrowNote && !lenderNet) note += `; ${escrowNote}`
            if (departing && rules.departingRental === "none") {
                net = -pitia
                note = "Departing residence: FHA allows rental income only with a >100-mile relocation and 25% equity"
            } else if (departing && rules.departingRental === "offset" && net > 0) {
                net = 0
                note += "; departing residence — rent may only offset its own payment"
            } else if (departing && rules.departingRental === "net") {
                note += "; departing residence — lease required"
            }
            // FHA's departing-home rule replaces the net outright; it is not combined with other rentals.
            if (rules.combineRentals && !(departing && rules.departingRental === "none")) {
                if (net) {
                    rentals.push({ id, partyIds: owners, label, net, note, propertyKey: item.key })
                } else if (departing) {
                    // Shown at $0 so the LO sees where the departing home went (its rent offset its payment).
                    push(income, { id, partyIds: owners, category: "rental", label, monthly: 0, note, propertyKey: item.key })
                }
                return
            }
            if (net >= 0) {
                if (net > 0) push(income, { id, partyIds: owners, category: "rental", label, monthly: net, note, propertyKey: item.key })
            } else {
                push(debts, { id, partyIds: owners, category: "property", label, monthly: -net, note: `${note} (net loss)`, propertyKey: item.key })
            }
            return
        }
        if (!pitia) return
        // Retained home, second home or other property with no rental income: full PITIA.
        if (isHome) {
            const occupantHome = owners.some((owner) => occupantIds.has(owner)) && subjectOccupancy === "principal"
            const inFront = !occupantHome && (subjectOccupancy !== "principal" || rules.nonOccupantHousingInFront)
            push(occupantHome ? debts : housing, {
                id, partyIds: owners, category: "property", label,
                monthly: pitia,
                note: [occupantHome
                    ? "Current home kept: full PITIA"
                    : `${subjectOccupancy !== "principal" ? "Primary residence" : "Non-occupant borrower's home"}: ${inFront ? "in the housing ratio" : "total DTI only"}`, escrowNote].filter(Boolean).join("; "),
                inFront,
                propertyKey: item.key,
            })
            return
        }
        push(debts, { id, partyIds: owners, category: "property", label, monthly: pitia, note: ["Full PITIA", escrowNote].filter(Boolean).join("; "), propertyKey: item.key })
    })

    // Combined net of the rental properties the LO has not unchecked: a positive total is income,
    // a negative total is a debt. Each property stays its own row, signed, in that one section,
    // so a loss offsets a gain instead of raising income and debts at the same time.
    if (rentals.length) {
        const combined = round2(rentals.filter((row) => !excluded.has(row.id)).reduce((sum, row) => sum + row.net, 0))
        const toIncome = combined >= 0
        const multiple = rentals.length > 1
        rentals.forEach((row) => {
            const signed = toIncome ? row.net : -row.net
            const rowNote = multiple
                ? `${row.note}; combined with the other rental properties (net ${combined >= 0 ? "" : "−"}$${Math.abs(combined).toLocaleString()})`
                : `${row.note}${toIncome ? "" : " (net loss)"}`
            push(toIncome ? income : debts, {
                id: row.id, partyIds: row.partyIds, category: toIncome ? "rental" : "property", label: row.label,
                monthly: signed, note: rowNote, propertyKey: row.propertyKey,
            })
        })
    }

    // ---- Present rent of people who will not live in the subject -------------
    const seenRent = new Set()
    participants.forEach(({ partyId, person, occupant }) => {
        if (`${person.residenceOwnership || ""}`.toLowerCase() !== "rent") return
        const rent = num(person.monthlyRent)
        if (!rent) return
        const keepsPaying = !occupant || subjectOccupancy !== "principal"
        if (!keepsPaying) return
        const street = addressKey(person.presentAddress?.street)
        if (street && seenRent.has(street)) return
        if (street) seenRent.add(street)
        push(housing, {
            id: `rent:${partyId}`,
            partyIds: [partyId],
            category: "rent",
            label: "Current rent",
            monthly: rent,
            inFront: subjectOccupancy !== "principal" || rules.nonOccupantHousingInFront,
            note: `${subjectOccupancy !== "principal" ? "Primary residence rent" : "Non-occupant borrower's rent"}: ${subjectOccupancy !== "principal" || rules.nonOccupantHousingInFront ? "in the housing ratio" : "total DTI only"}`,
        })
    })

    // ---- Subject property ------------------------------------------------------
    const payment = options.payment || {}
    const subjectRows = []
    if (num(payment.total)) {
        const b = payment.breakdown || {}
        const parts = [payment.isEstimate ? "Estimated — no rate selected" : ""]
        if (b.upfrontFee) parts.push(`P&I on $${Math.round(b.totalLoanAmount).toLocaleString()} incl. financed ${b.upfrontLabel} ${(b.upfrontRate * 100).toFixed(2)}%`)
        if (b.pmi) parts.push(`MI $${Math.round(b.pmi).toLocaleString()} (${b.miSource === "product" ? "from the selected product" : b.miSource === "entered" ? "entered in the estimate" : b.miSource === "annual premium" ? `${(b.annualMIRate * 100).toFixed(2)}% annual premium` : "from the 1003"})`)
        subjectRows.push({ id: "subject:pitia", label: "Proposed payment (PITIA)", monthly: num(payment.total), note: parts.filter(Boolean).join("; ") })
    }
    const seenNewLoans = new Set()
    participants.forEach(({ partyId }) => {
        list(finance.liabilitiesByPartyId?.[partyId]?.newMortgages).forEach((loan, index) => {
            const key = `${loan.creditor || ""}|${loan.monthlyAmount || ""}|${loan.lienType || ""}`
            if (!num(loan.monthlyAmount) || seenNewLoans.has(key)) return
            seenNewLoans.add(key)
            subjectRows.push({ id: `subject:newloan:${seenNewLoans.size - 1}`, label: `Other new mortgage${loan.creditor ? ` (${loan.creditor})` : ""}`, monthly: num(loan.monthlyAmount) })
        })
    })
    // On a refinance the existing liens are paid off by this loan unless marked otherwise.
    // The credit import marks every open mortgage "not paid off", including the first lien
    // this refinance replaces; that one (the largest balance the new loan covers) is paid off.
    const subjectLiens = property.purpose === "purchase" ? [] : list(property.loans)
    const newLoanAmount = num(application?.selection?.loansize)
    const firstLien = subjectLiens.reduce((best, loan) => (num(loan.remainingBalance) > num(best?.remainingBalance) ? loan : best), null)
    const refinancedLien = firstLien && (!newLoanAmount || newLoanAmount >= num(firstLien.remainingBalance)) ? firstLien : null
    subjectLiens.forEach((loan, index) => {
        if (`${loan.paidOffAtClosing || ""}`.toLowerCase() !== "no") return
        if (loan === refinancedLien) {
            warnings.push({ code: "firstLienKept", text: `${loan.name || loan.creditor || "The existing first mortgage"} is marked as not paid off, but this refinance pays it off; it is left out of DTI. Check 1003 Section 3` })
            return
        }
        if (num(loan.monthlyPayment)) subjectRows.push({ id: `subject:lien:${loan.key || index}`, label: `Existing lien kept (${loan.name || loan.creditor || "lien"})`, monthly: num(loan.monthlyPayment), note: "Marked as not paid off by this loan" })
    })
    if (property.isManufacturedHome === true && num(property.mobilehomeinfo?.leasedSpaceRentAmount)) {
        subjectRows.push({ id: "subject:spacerent", label: "Lot / space rent", monthly: num(property.mobilehomeinfo.leasedSpaceRentAmount) })
    }
    const allIds = participants.map((p) => p.partyId)
    const subjectRent = property.purpose === "purchase"
        ? num(property.purchase?.expectedgrossmonthlyrentalincome)
        : num(property.grossrentalincome)
    if (subjectOccupancy === "investment" && options.pricing) {
        // Pricing adds the product's P&I + MI itself and cannot net rent against
        // them, so the rent goes to income at 75% and the rest of PITIA to debts.
        subjectRows.forEach((row) => push(debts, { ...row, partyIds: allIds, category: "subject" }))
        if (subjectRent) push(income, { id: "subject:rent", partyIds: allIds, category: "rental", label: "Subject property rent", monthly: 0.75 * subjectRent, note: "75% of rent" })
    } else if (subjectOccupancy === "investment") {
        // Investment subject: 75% of rent netted against its PITIA, counted once.
        const pitia = subjectRows.reduce((total, row) => total + row.monthly, 0)
        // Purchases keep the LO's net figure under purchase.expectednetmonthlyrentalincome.
        const enteredNet = property.purpose === "purchase" ? property.purchase?.expectednetmonthlyrentalincome : property.netrentalincome
        const lenderNet = enteredNet !== "" && enteredNet != null && (subjectRent || num(enteredNet))
        const net = lenderNet ? num(enteredNet) : 0.75 * subjectRent - pitia
        const note = lenderNet
            ? `Net rental income from the 1003; PITIA $${round2(pitia).toLocaleString()}`
            : `75% of $${subjectRent.toLocaleString()} rent − $${round2(pitia).toLocaleString()} PITIA`
        if (net >= 0) {
            if (net > 0) push(income, { id: "subject:net", partyIds: allIds, category: "rental", label: "Subject property net rent", monthly: net, note })
        } else {
            push(debts, { id: "subject:net", partyIds: allIds, category: "subject", label: "Subject property net loss", monthly: -net, note })
        }
    } else {
        subjectRows.forEach((row) => push(subjectOccupancy === "principal" ? housing : debts, {
            ...row, partyIds: allIds, category: "subject", inFront: subjectOccupancy === "principal",
        }))
        if (subjectOccupancy === "principal" && subjectRent && (property.propertytype === "twotofourfamily" || property.propertytype === "twotofour")) {
            push(income, { id: "subject:rent", partyIds: allIds, category: "rental", label: "Subject property rent (other units)", monthly: 0.75 * subjectRent, note: "75% of rent; does not reduce the subject payment" })
        }
    }

    // ---- Tradelines (2c), each counted once --------------------------------------
    const grossIncome = income.filter((row) => !row.excluded).reduce((sum, row) => sum + row.monthly, 0)
    const seenLoans = new Set()
    // A joint account pulled on two individual credit reports arrives under two keys
    // (credit-report:<report>:<account>); same account, creditor and payment = one debt.
    const seenAccounts = new Map()
    const tradelines = []
    Object.entries(finance.liabilitiesByPartyId || {}).forEach(([partyId, liabilities]) => {
        list(liabilities?.loans).forEach((loan, index) => {
            const key = loan.key || loan.accountNumber || `${partyId}:${index}`
            if (seenLoans.has(key)) return
            seenLoans.add(key)
            if (isPropertyLoan(loan)) return
            if (`${loan.paidOffAtClosing || ""}`.toLowerCase() === "yes") return
            const owners = list(loan.ownerPartyIds).length ? loan.ownerPartyIds : [partyId]
            const account = accountOf(loan) && `${accountOf(loan)}|${num(loan.monthlyPayment)}`
            const first = account && seenAccounts.get(account)
            if (first) {
                first.ownerPartyIds = [...new Set([...first.ownerPartyIds, ...owners])]
                return
            }
            const tradeline = { ...loan, key, ownerPartyIds: owners }
            if (account) seenAccounts.set(account, tradeline)
            tradelines.push(tradeline)
        })
    })
    const shortTermTotal = tradelines
        .filter((loan) => `${loan.creditLiabilityAccountType || ""}` === "Installment" && num(loan.monthsLeftToPay) > 0 && num(loan.monthsLeftToPay) <= 10)
        .reduce((sum, loan) => sum + num(loan.monthlyPayment), 0)
    tradelines.forEach((loan) => {
        const type = `${loan.creditLiabilityAccountType || ""}`
        const payment = num(loan.monthlyPayment)
        const balance = num(loan.remainingBalance)
        const label = loan.name || loan.creditor || "Debt"
        const row = { id: `debt:${loan.key}`, partyIds: loan.ownerPartyIds, category: "tradeline", label }
        const isStudent = STUDENT_LOAN_PATTERN.test(`${label} ${loan.creditLoanType || ""}`)
        const monthsLeft = num(loan.monthsLeftToPay)

        if (isStudent && !payment && balance) {
            if (rules.studentAnnualPct) {
                push(debts, { ...row, monthly: (balance * rules.studentAnnualPct) / 12, note: "Student loan, no payment: 5% of balance ÷ 12 (excluded only if deferred 12+ months past closing)" })
            } else {
                push(debts, { ...row, monthly: balance * rules.studentZeroPaymentPct, note: `Student loan, no payment: ${rules.studentZeroPaymentPct * 100}% of balance` })
            }
            return
        }
        if (type === "Installment" && monthsLeft > 0 && payment) {
            const short = rules.installmentShortTerm === "lt10" ? monthsLeft < 10 : monthsLeft <= 10
            const excludeShort = short && (rules.installmentShortTerm !== "le10and5pct" || shortTermTotal <= 0.05 * grossIncome)
            if (excludeShort) {
                push(debts, { ...row, monthly: 0, note: `${monthsLeft} payments left: excluded`, ruleExcluded: true })
                return
            }
        }
        if (type === "Open") {
            if (rules.open30 === "exclude") { push(debts, { ...row, monthly: 0, note: "30-day account: not counted (verify funds to pay the balance)", ruleExcluded: true }); return }
            if (rules.open30 === "fullBalance") { push(debts, { ...row, monthly: balance, note: "30-day account: full balance" }); return }
            if (rules.open30 === "fivePct") { push(debts, { ...row, monthly: payment || balance * 0.05, note: payment ? "" : "30-day account: 5% of balance" }); return }
        }
        if (type === "Revolving" && !payment && balance) {
            push(debts, { ...row, monthly: balance * rules.revolvingNoPaymentPct, note: "No payment reported: 5% of balance" })
            return
        }
        if (!payment) return
        push(debts, { ...row, monthly: payment, note: type === "Mortgage" ? "Lease" : "" })
    })

    // ---- Alimony / child support / other obligations (2d) -----------------------
    // The V2 GET returns 2d as participantsById[id].income.alimony; additionalExpenses
    // only exists in a save request (same fallback as the 1003 page).
    participants.forEach(({ partyId, person }) => {
        const saved = list(finance.liabilitiesByPartyId?.[partyId]?.additionalExpenses)
        const obligations = saved.length ? saved : list(person.income?.alimony)
        obligations.forEach((item, index) => {
            const amount = num(item.amount)
            if (!amount) return
            const kind = item.alimonyorsupport || "other"
            const row = { id: `obl:${partyId}:${item.key || index}`, partyIds: [partyId], category: "obligation", label: OTHER_LIABILITY_LABELS[kind] || "Other obligation" }
            const shortTerm = `${item.lessthan10 || ""}`.toLowerCase() === "yes" || (num(item.monthstopay) > 0 && num(item.monthstopay) <= 10)
            // Fannie applies the ten-month rule to alimony and separate maintenance too (B3-6-05 p.533).
            const tenMonthRule = kind === "childsupport" ? program !== "fha" : program === "fnma" && (kind === "alimony" || kind === "maintenancepayments")
            if (tenMonthRule && shortTerm) {
                push(debts, { ...row, monthly: 0, note: "10 or fewer payments left: excluded", ruleExcluded: true })
                return
            }
            if ((kind === "alimony" || kind === "maintenancepayments") && rules.alimonyAsIncomeReduction) {
                push(income, { ...row, category: "obligation", monthly: -amount, note: "Freddie Mac: alimony reduces income" })
                return
            }
            push(debts, { ...row, monthly: amount })
        })
    })

    // ---- Totals ------------------------------------------------------------------
    const counted = (rows) => rows.filter((row) => !row.excluded)
    const sum = (rows) => round2(counted(rows).reduce((total, row) => total + row.monthly, 0))
    const occupantOnly = (rows) => rows.filter((row) => row.partyIds.some((id) => occupantIds.has(id)))

    const incomeTotal = sum(income)
    const frontRows = housing.filter((row) => row.inFront)
    const frontTotal = sum(frontRows)
    const obligationsTotal = round2(sum(housing) + sum(debts))
    const ratio = (part, whole) => (whole > 0 ? round2((part / whole) * 100) : null)

    const hasNonOccupant = participants.some((p) => !p.occupant) && subjectOccupancy === "principal"
    const occupantIncomeRows = income.filter((row) => row.partyIds.every((id) => occupantIds.has(id)) || row.id === "subject:rent")
    const occupantIncome = sum(occupantIncomeRows)
    const occupantFront = sum(occupantOnly(frontRows))
    const occupantObligations = round2(sum(occupantOnly(housing)) + sum(occupantOnly(debts)))

    const totals = {
        income: incomeTotal,
        housing: frontTotal,
        obligations: obligationsTotal,
        frontRatio: ratio(frontTotal, incomeTotal),
        backRatio: ratio(obligationsTotal, incomeTotal),
        occupantIncome: hasNonOccupant ? occupantIncome : null,
        occupantFrontRatio: hasNonOccupant ? ratio(occupantFront, occupantIncome) : null,
        occupantBackRatio: hasNonOccupant ? ratio(occupantObligations, occupantIncome) : null,
    }

    const limits = rules.limits[underwriting] || {}
    if (limits.front && totals.frontRatio > limits.front) warnings.push({ code: "frontOverLimit", text: `Housing ratio ${totals.frontRatio}% is over the ${limits.front}% ${PROGRAM_LABELS[program]} limit` })
    if (limits.back && totals.backRatio > limits.back) warnings.push({ code: "backOverLimit", text: `DTI ${totals.backRatio}% is over the ${limits.back}% ${PROGRAM_LABELS[program]} limit` })
    // Primary-residence loan where every borrower answered No to Declarations 5a: the data contradicts itself.
    const nobodyOccupies = subjectOccupancy === "principal" && participants.length > 0 && participants.every((p) => !p.occupant)
    if (nobodyOccupies) {
        warnings.push({ code: "noOccupant", text: "The loan is for a primary residence, but no borrower answered Yes to \"Will you occupy the property as your primary residence?\" (Declarations 5a)" })
    } else if (hasNonOccupant && !(occupantIncome > 0)) {
        warnings.push({ code: "noOccupantIncome", text: "The occupying borrowers have no income, so occupant-only DTI can't be calculated" })
    }
    if (hasNonOccupant && limits.occupantBack && totals.occupantBackRatio > limits.occupantBack) warnings.push({ code: "occupantOverLimit", text: `Occupant-only DTI ${totals.occupantBackRatio}% is over the ${limits.occupantBack}% limit for a non-occupant co-borrower` })
    if (hasNonOccupant && limits.occupantFront && totals.occupantFrontRatio > limits.occupantFront) warnings.push({ code: "occupantFrontOverLimit", text: `Occupant housing ratio ${totals.occupantFrontRatio}% is over the ${limits.occupantFront}% limit` })
    if (program === "va") warnings.push({ code: "vaResidualIncome", text: "VA: also check residual income" })
    if ((program === "fha" || program === "va") && COMMUNITY_PROPERTY_STATES.includes(`${property.state || ""}`.trim().toUpperCase())) {
        warnings.push({ code: "communityProperty", text: "Community-property state: include a non-borrowing spouse's debts" })
    }
    if (payment.isEstimate) warnings.push({ code: "estimatedPayment", text: "No rate selected — the payment and DTI are estimates" })
    // The estimator's breakdown (header and calculator; pricing passes none).
    const breakdown = payment.breakdown
    if (breakdown && !options.pricing) {
        if (!num(breakdown.totalLoanAmount)) {
            warnings.push({ code: "noLoanAmount", text: "No loan amount, so the proposed payment is $0 — enter the property value and down payment (1003 Section 4a, or in the estimated payment)" })
        } else if (!num(breakdown.propertyTaxes) && !num(breakdown.insurance)) {
            warnings.push({ code: "noTaxesInsurance", text: "The proposed payment has no property taxes or homeowner's insurance — add them in the estimated payment" })
        }
    }
    if (escrowUnknown.length) {
        const where = escrowUnknown.length > 2 ? `${escrowUnknown.slice(0, 2).join(", ")} and ${escrowUnknown.length - 2} more` : escrowUnknown.join(" and ")
        warnings.push({ code: "escrowUnknown", text: `Does the mortgage payment on ${where} already include taxes and insurance? Answer it in 1003 Section 3 so they are not counted twice` })
    }
    if (payment.breakdown?.miSource === "missing") warnings.push({ code: "miMissing", text: "LTV is over 80% but the payment has no mortgage insurance — select a rate or enter MI" })
    if (income.some((row) => row.needsCashFlow && !row.excluded)) warnings.push({ code: "selfEmployedCashFlow", text: "Self-employed income: confirm it with a 2-year tax-return cash-flow analysis (Form 1084 / 91)" })

    return {
        program,
        underwriting,
        limits,
        participants: participants.map(({ person, ...rest }) => rest),
        income,
        housing,
        debts,
        totals,
        warnings,
    }
}

/** 1003 V2 section anchor for a row (used by the calculator's "go to 1003" links). */
export const sectionForRow = (row) => {
    const id = row?.id || ""
    if (id.startsWith("inc:") && id.includes(":job:")) return row.jobIndex ? "1c" : "1b"
    if (id.startsWith("inc:")) return "1e"
    if (id.startsWith("reo:")) return "3a"
    if (id.startsWith("rent:")) return "1a"
    if (id.startsWith("subject:newloan")) return "4b"
    if (id.startsWith("subject:rent")) return "4c"
    if (id.startsWith("subject:")) return "4a"
    if (id.startsWith("debt:")) return "2c"
    if (id.startsWith("obl:")) return "2d"
    return ""
}

/** Rows a participant tab shows: anything they own or share. */
export const rowsForParticipant = (rows, partyId) => rows.filter((row) => !partyId || row.partyIds.includes(partyId))
